import type { CarrierListStateInterface } from '@/types/carrier-types';
import type { StoreBaseResponse } from '@/types/types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { Nullable } from 'primereact/ts-helpers';
import { processDate } from './date-utils';

export function processAdditionalCostFieldForApi(str: string) {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map((item) => item.trim())
    .join('_');
}

export function processChassis(
  chassisModel: CarrierListStateInterface['chassisModel'],
) {
  const chassisModelName = chassisModel.model;
  const chassisNo = chassisModel.chassisNo;
  let chassis = null;
  if (chassisModelName && chassisNo) {
    chassis = `${chassisModelName}--${chassisNo}`;
  } else if (chassisModelName) {
    chassis = `${chassisModelName}--`;
  } else if (chassisNo) {
    chassis = `--${chassisNo}`;
  }

  return chassis;
}

export function processDateRangeForApi(dateRange: string[] | null) {
  // convert date to date.toISOString() before passing to this function
  const fromDt = dateRange?.[0] ? dateRange[0] : null;
  const toDt = dateRange?.[1] ? dateRange[1] : null;
  const formattedFromDt = fromDt
    ? processDate(fromDt, 'api').formattedDate
    : null;
  const formattedToDt = toDt ? processDate(toDt, 'api').formattedDate : null;

  let dateRangeInString = null; //final looks like: 2010-10-10--2015-11-11
  if (formattedFromDt && formattedToDt) {
    dateRangeInString = `${formattedFromDt}--${formattedToDt}`;
  } else if (formattedFromDt) {
    dateRangeInString = `${formattedFromDt}--${formattedFromDt}`;
  }
  return dateRangeInString;
}

export function processStockRange(
  stockRange: CarrierListStateInterface['stockRange'],
) {
  const stkFrom = stockRange.from || undefined;
  const stkTo = stockRange.to || undefined;
  let stockNumber = null;
  if (stkFrom && stkTo) {
    stockNumber = `${stkFrom}-${stkTo}`;
  } else if (stkFrom) {
    stockNumber = `${stkFrom}`;
  }
  return stockNumber;
}

export const processApiErrors = <T>(
  setError: (name: T, error: { message: string }) => void,
  array: { type: string; loc: string[]; msg: string; input: string }[],
) => {
  array.forEach((m) => {
    let message = '';
    if (typeof m?.msg === 'string') {
      message = m.msg;
    } else {
      message = JSON.stringify(m.msg);
    }
    setError(m.loc[1] as T, {
      message: message,
    });
  });
};

export const processApiErrorsMapped = <T>(
  setError: (name: T, error: { message: string }) => void,
  array: { type: string; loc: string[]; msg: string; input: string }[],
  mapper: { [key: string]: string },
) => {
  array.forEach((m) => {
    setError(mapper[m.loc[1]] as T, {
      message: m.msg,
    });
  });
};

export const checkIsError = (value: Nullable<string | number>) => {
  if (Number(value) === 0) return false; // handles blank and zero Number("")==0
  return true;
};

export const createCustomError = <T = unknown>(
  error: T,
): { error: FetchBaseQueryError } => {
  const err = {
    status: 'CUSTOM_ERROR',
    error: 'yes',
    data: null,
  } as FetchBaseQueryError;
  if (error && typeof error === 'object' && 'detail' in error) {
    err.data = error.detail;
    return {
      error: err,
    };
  } else {
    err.data = error;
    return { error: err };
  }
};

export const createCustomResponse = <T = unknown>(
  data: StoreBaseResponse<T>,
) => {
  if (data.error) {
    return createCustomError(data.message);
  } else {
    return {
      data,
    };
  }
};
